import { Fragment, type ReactNode } from 'react'

/**
 * A deliberately small markdown subset for text a form designer writes (static text, help text,
 * subtitles): **bold**, *italic*, [link](https://…), line breaks, blank-line paragraphs and
 * "- " / "1. " lists. Everything becomes React elements, never HTML, so nothing typed can inject markup.
 */

const INLINE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|\[[^\]\n]+\]\([^)\s]+\))/g
const SAFE_URL = /^(https?:\/\/|mailto:)/i
const BULLET = /^- /
const NUMBERED = /^\d+\. /

function inline(text: string, keyBase: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const key = `${keyBase}-${i}`
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key}>{inline(part.slice(2, -2), key)}</strong>
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={key}>{inline(part.slice(1, -1), key)}</em>
    }
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part)
    if (link) {
      return SAFE_URL.test(link[2])
        ? <a key={key} href={link[2]} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">{link[1]}</a>
        : <Fragment key={key}>{link[1]}</Fragment>
    }
    return <Fragment key={key}>{part}</Fragment>
  })
}

function lines(block: string, keyBase: string): ReactNode[] {
  return block.split('\n').flatMap((line, i) => (i === 0 ? [] : [<br key={`${keyBase}-br${i}`} />]).concat(
    <Fragment key={`${keyBase}-l${i}`}>{inline(line, `${keyBase}-${i}`)}</Fragment>
  ))
}

export function renderSimpleMarkdown(text: string): ReactNode {
  const blocks = text.replace(/\r\n?/g, '\n').split(/\n{2,}/).map((b) => b.trim()).filter(Boolean)
  return blocks.map((block, b) => {
    const rows = block.split('\n')
    if (rows.every((r) => BULLET.test(r))) {
      return <ul key={b} className="list-disc pl-5 space-y-0.5">{rows.map((r, i) => <li key={i}>{inline(r.replace(BULLET, ''), `${b}-${i}`)}</li>)}</ul>
    }
    if (rows.every((r) => NUMBERED.test(r))) {
      return <ol key={b} className="list-decimal pl-5 space-y-0.5">{rows.map((r, i) => <li key={i}>{inline(r.replace(NUMBERED, ''), `${b}-${i}`)}</li>)}</ol>
    }
    return <p key={b}>{lines(block, String(b))}</p>
  })
}

export type MarkdownAction = 'bold' | 'italic' | 'link' | 'bullets' | 'numbers'

export interface TextEdit { text: string; selStart: number; selEnd: number }

/** Applies a toolbar action to the selected part of `text` and says where the selection should land. */
export function applyMarkdown(text: string, start: number, end: number, action: MarkdownAction): TextEdit {
  const selected = text.slice(start, end)
  const before = text.slice(0, start)
  const after = text.slice(end)

  if (action === 'bold' || action === 'italic') {
    const mark = action === 'bold' ? '**' : '*'
    const inner = selected || (action === 'bold' ? 'bold text' : 'italic text')
    return { text: `${before}${mark}${inner}${mark}${after}`, selStart: start + mark.length, selEnd: start + mark.length + inner.length }
  }
  if (action === 'link') {
    const label = selected || 'link text'
    const url = 'https://'
    const out = `${before}[${label}](${url})${after}`
    const urlStart = start + label.length + 3
    return { text: out, selStart: urlStart, selEnd: urlStart + url.length }
  }
  const lineStart = before.lastIndexOf('\n') + 1
  const block = text.slice(lineStart, end || start)
  const prefixed = (block || 'List item').split('\n').map((l, i) => (action === 'bullets' ? '- ' : `${i + 1}. `) + l).join('\n')
  const out = text.slice(0, lineStart) + prefixed + after
  return { text: out, selStart: lineStart, selEnd: lineStart + prefixed.length }
}
